import { useCallback, useEffect, useRef } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { usePrioritizeTagsQueryParam } from './usePrioritizeTagsQueryParam';
import { useUpdateAccommodationsConsoleSearchParams } from './useUpdateAccommodationsConsoleSearchParams';
import { PRIORITIZE_TAG_QUERY_PARAM_KEY } from './constants';

/**
 * Writes the tag selection to the URL.
 *
 * `replace`, not `push`: toggling a tag is refining the current search, not navigating. Pushing
 * would make Back step through each checkbox click instead of leaving the search.
 */
const useWritePrioritizeTags = () => {
  const { applyAccommodationConsoleSearchParamsToUrl } = useUpdateAccommodationsConsoleSearchParams();

  return useEventCallback((tags: ReadonlyArray<string>) => {
    applyAccommodationConsoleSearchParamsToUrl({
      // An empty selection deletes the key — the updater treats '' as a delete — which is exactly
      // right: no key means no tag prioritization at all.
      searchParams: { [PRIORITIZE_TAG_QUERY_PARAM_KEY]: tags.length ? tags.join(',') : '' },
      actionType: 'replace',
    });
  });
};

/**
 * Seeds a product page's own tags into the URL once on mount, if the link did not carry any.
 *
 * Call this in EXACTLY ONE place (the console root). The URL is the single source of truth for
 * which tags lead — the request, the sort and the filter all read it through
 * `usePrioritizeTagsQueryParam` — and this is the only thing that puts a page default there.
 *
 * Seeding rather than merging the default in at read time is what lets the tag filter turn a tag
 * OFF. Merging on every read (the previous behaviour) meant unchecking wrote the URL and the
 * default immediately put the tag back, so the checkbox could never stay unchecked.
 *
 * At most once per mount, so after the first paint the guest owns the URL. A reload re-seeds, which
 * is what we want: `/honeymoon` should open leading with honeymoon stays every time, however the
 * guest left it last.
 *
 * The trade-off: a link can now strip a product page's own tag
 * (`/honeymoon/hotel/search?prioritizeTag=luxury` leads with luxury alone). That was previously
 * impossible by construction, but it is the same capability the filter itself offers, so defending
 * against it in the URL while exposing it in the UI would only be inconsistent.
 */
export const useSeedDefaultPrioritizeTags = (defaultPrioritizeTags?: ReadonlyArray<string>): void => {
  const prioritizeTags = usePrioritizeTagsQueryParam();
  const writePrioritizeTags = useWritePrioritizeTags();
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (hasSeededRef.current || !defaultPrioritizeTags?.length) {
      return;
    }

    // Mark the mount handled BEFORE the inbound check, not after the write. Every real entry point
    // into this console arrives with the tag already in the URL — the landing page writes
    // `prioritizeTag=honeymoon` into the query before it navigates, and so does the in-event
    // "Unlock Offers" link — so this effect returns here on mount. Leaving the ref false meant that
    // when the guest then unchecked the tag, the now-empty selection re-ran this effect as if the
    // page had never seeded, and the default went straight back into the URL: the checkbox could
    // not be turned off on the one flow the feature actually ships.
    hasSeededRef.current = true;

    if (prioritizeTags.length) {
      return;
    }

    writePrioritizeTags(defaultPrioritizeTags);
  }, [defaultPrioritizeTags, prioritizeTags, writePrioritizeTags]);
};

/**
 * Current tag selection plus the toggle the tag filter drives, both backed by the URL.
 *
 * Read-only consumers (the search request, the client-side sort) use
 * `usePrioritizeTagsQueryParam` directly instead — they must not be able to write.
 */
export const usePrioritizeTagsFilter = () => {
  const prioritizeTags = usePrioritizeTagsQueryParam();
  const writePrioritizeTags = useWritePrioritizeTags();

  const togglePrioritizeTag = useCallback(
    (tag: string, isSelected: boolean) => {
      const next = isSelected ? Array.from(new Set([...prioritizeTags, tag])) : prioritizeTags.filter(current => current !== tag);
      writePrioritizeTags(next);
    },
    [prioritizeTags, writePrioritizeTags],
  );

  return { prioritizeTags, togglePrioritizeTag };
};
